import React, { FC } from 'react';

import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import Tippy from '@tippyjs/react';

import { MediaInput } from '@components/common';
import { DragSVG, TrashSVG } from '@components/svgs';

export interface FieldProps {
  field: FieldData;
  placeholder?: string;
  onChange?: (field: FieldData) => void;
  onBlur?: (field: FieldData) => void;
  onDelete: (field: FieldData) => void;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  hideControls?: boolean;
  onEnterKeyPress?: () => void;
  type: string;
  onPaste?: (args: { e: React.ClipboardEvent<HTMLInputElement>; position: number }) => void;
}

export const Field: FC<React.PropsWithChildren<FieldProps>> = ({
  field: { position, label },
  placeholder,
  onChange,
  onBlur,
  onDelete,
  dragHandleProps,
  hideControls,
  onEnterKeyPress,
  type,
  onPaste
}) => {
  const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
    onChange?.({ position, label: event.currentTarget.value });
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.({ position, label: event.currentTarget.value });
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    onPaste?.({ position, e });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      onEnterKeyPress?.();
    }
  };

  return (
    <MediaInput
      name={`field-${type}-${position}`}
      wrapperClassName='w-full mb-1'
      value={label}
      placeholder={placeholder}
      onPaste={handlePaste}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      end={
        !hideControls ? (
          <div className='flex h-full items-center space-x-1 pr-3'>
            <Tippy maxWidth={240} arrow={false} content='Delete'>
              <button
                type='button'
                className='flex h-6 w-6 items-center justify-center rounded-full hover:bg-gray-100'
                onClick={() => onDelete({ position, label })}
                aria-label={`Delete ${label}`}
              >
                <TrashSVG className='text-gray-700' />
              </button>
            </Tippy>
            <Tippy maxWidth={240} arrow={false} content='Drag to reorder'>
              <button
                type='button'
                className='flex h-6 w-6 items-center justify-center rounded-full hover:bg-gray-100'
                {...dragHandleProps}
              >
                <DragSVG className='text-gray-700' />
              </button>
            </Tippy>
          </div>
        ) : null
      }
    />
  );
};
